import { makeAutoObservable, flow, runInAction } from 'mobx';
import { Address, Customer, CustomerModel } from '../models/Customer';
import { getCustomers, updateCustomer, addCustomer, deleteCustomer } from '../services/CustomersAPI';
import { ReferenceData } from '../models/common';
import { errorStore } from './ErrorStore';
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class CustomerStore {
  data: Customer[] = [];
  isLoading = false;
  currentRow: CustomerModel | undefined
  showModal = false;
  addressRow: Customer | undefined;
  showAddressModal = false;

  constructor() {
    makeAutoObservable(this);
  }

  loadData = flow(function* (this: CustomerStore) {
    if (this.data.length || this.isLoading) return;
    const departmentCode = getActiveDepartmentCode();

    try {
      this.setLoading(true);
      const response = yield getCustomers();
      if (departmentCode !== getActiveDepartmentCode()) {
        return;
      }

      this.setData(response || []);
    } catch (error: any) {
      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  });

  async reloadForActiveDepartment() {
    this.reset();
    await this.loadData();
  }

  async delete(customer: Customer) {
    try {
      this.setLoading(true);
      await deleteCustomer(customer.id);
      this.setData(this.data.filter(c => c.id !== customer.id));
      this.clearForm();
    } catch (error: any) {
      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  async update(customer: Customer) {
    try {
      this.setLoading(true);
      await updateCustomer(customer.id, customer);

      runInAction(() => {
        this.data = this.data.map(c => c.id === customer.id ? { ...customer } : c);
      });

      this.removeCurrent();
    } catch (error: any) {
      this.setError(error.message);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async addNew(newCustomer: Customer, onSuccess?: () => void) {
    try {
      this.setLoading(true);
      const addedCustomer = await addCustomer(newCustomer);
      if (addedCustomer) {
        this.addRow(addedCustomer);
        this.clearForm();
        onSuccess?.();
      }
    } catch (error: any) {
      this.setError(error.message)
    } finally {
      this.setLoading(false);
    }
  }

  saveAddress = async (address: Address) => {
    const target = this.data.find(c => c.id === this.addressRow?.id);
    if (!target) return;
    try {
      await this.update({ ...target, address });
      this.closeAddress();
    } catch {
      this.setError('Error saving address');
    }
  };

  setupNew = (name?: string) => {
    this.setCurrent(new CustomerModel({ id: '', name: name || '' }))
    this.setShowModal(true);
  };

  get options() {
    return this.data as ReferenceData[];
  };

  setCurrent(currentCustomer?: CustomerModel) {
    this.currentRow = currentCustomer;
  }

  removeCurrent() {
    this.currentRow = undefined;
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string) {
    errorStore.report(error);
  }

  addRow(customer: Customer) {
    this.data = [customer, ...this.data];
  }

  setShowModal(state: boolean) {
    this.showModal = state;
  }

  openAddress = (customer: Customer) => {
    this.addressRow = customer;
    this.showAddressModal = true;
  };

  closeAddress = () => {
    this.addressRow = undefined;
    this.showAddressModal = false;
  };

  clearForm() {
    this.removeCurrent();
    this.setShowModal(false);
    this.showAddressModal = false;
  }

  reset() {
    this.data = [];
    this.isLoading = false;
    this.clearForm();
  }

  setData(records: Customer[]) {
    this.data = records;
  }
}

export const customerStore = new CustomerStore();
